// Frontend view model only, NOT a backend wire contract. The future adapter must
// map the published unified roster into this shape; never merge partial lists.
export const presenceState = value => ['online', 'offline'].includes(value) ? value : 'unknown';

export function projectRoster(snapshot) {
  if (!snapshot) return { effectivePresence: 'unknown', groups: [] };
  const rows = (snapshot.staff || []).map(row => ({
    ...row,
    srmPresence: presenceState(row.srmPresence),
    watiPresence: presenceState(row.executionMode === 'shared'
      ? snapshot.serviceAccounts?.[row.serviceAccountId]?.presence : row.watiPresence),
  }));
  const actor = rows.find(row => String(row.userId) === String(snapshot.actorId));
  return {
    effectivePresence: actor?.watiPresence || 'unknown',
    groups: ['individual', 'shared'].map(mode => ({ mode, rows: rows.filter(row => row.executionMode === mode) })),
  };
}

const confirmedTransition = (before, after) => presenceState(before) !== 'unknown'
  && presenceState(after) !== 'unknown' && before !== after;

export function rosterTransitions(previous, next) {
  if (!previous || !next) return [];
  const changes = [];
  const oldRows = projectRoster(previous).groups.flatMap(group => group.rows);
  projectRoster(next).groups.flatMap(group => group.rows).forEach(row => {
    const old = oldRows.find(item => String(item.userId) === String(row.userId));
    if (!old) return;
    ['srmPresence', ...(row.executionMode === 'individual' && old.executionMode === 'individual' ? ['watiPresence'] : [])].forEach(field => {
      if (confirmedTransition(old[field], row[field])) changes.push({ entity: `staff:${row.userId}:${field}`, name: row.displayName, presence: row[field] });
    });
  });
  Object.entries(next.serviceAccounts || {}).forEach(([id, account]) => {
    if (confirmedTransition(previous.serviceAccounts?.[id]?.presence, account.presence)) {
      changes.push({ entity: `service:${id}:watiPresence`, name: account.displayName, presence: account.presence });
    }
  });
  return changes;
}

// Injected transport is deliberately not wired until backend publishes its
// endpoint/subscription. Each subscribe returns cleanup. deliveryKey must be a
// stable occurrence identity supplied by that adapter, not a guessed timestamp.
export function bindStaffRoster({ transport, publish, notify, claimNotification = () => false }) {
  let active = true;
  let generation = 0;
  let previous = null;
  let stopSnapshots;
  function reconnect() {
    const current = ++generation;
    stopSnapshots?.();
    previous = null;
    publish(null);
    const receive = ({ snapshot, deliveryKey }) => {
      if (!active || current !== generation || !snapshot) return;
      const transitions = rosterTransitions(previous, snapshot);
      previous = snapshot;
      publish(snapshot);
      if (notify && deliveryKey) transitions.forEach(change => {
        if (claimNotification(`${deliveryKey}:${change.entity}`)) notify(change);
      });
    };
    stopSnapshots = transport.subscribe(receive);
    // The adapter must order its initial complete snapshot and subsequent
    // updates. No separate HTTP fetch can race and overwrite newer snapshots.
  }
  const stopReconnect = transport.onReconnect(reconnect);
  reconnect();
  return () => { active = false; generation++; stopSnapshots?.(); stopReconnect(); };
}
